<template>
  <div
    ref="bookingSearchBar"
    class="search-bar"
    :class="{ glass: isBookingHome }"
  >
    <button
      v-if="!isDesktop && bookingPlaces?.shouldShowDefaultPanel"
      class="back-btn"
      @click="togglePlacesPanel"
    >
      <svg
        xmlns="http://www.w3.org/2000/svg"
        width="9"
        height="10"
        viewBox="0 0 9 10"
        fill="none"
      >
        <path
          d="M8.53817 0.242327C8.38203 0.0871179 8.17082 0 7.95066 0C7.73051 0 7.5193 0.0871179 7.36316 0.242327L4.3715 3.19233L1.4215 0.242327C1.26536 0.0871179 1.05415 0 0.833999 0C0.613844 0 0.402634 0.0871179 0.246499 0.242327C0.168392 0.319796 0.106396 0.411964 0.0640889 0.513513C0.0217817 0.615062 0 0.723984 0 0.833994C0 0.944004 0.0217817 1.05292 0.0640889 1.15447C0.106396 1.25602 0.168392 1.34819 0.246499 1.42566L3.77983 4.95899C3.8573 5.0371 3.94947 5.0991 4.05102 5.1414C4.15257 5.18371 4.26149 5.20549 4.3715 5.20549C4.48151 5.20549 4.59043 5.18371 4.69198 5.1414C4.79353 5.0991 4.8857 5.0371 4.96316 4.95899L8.53817 1.42566C8.61627 1.34819 8.67827 1.25602 8.72057 1.15447C8.76288 1.05292 8.78466 0.944004 8.78466 0.833994C8.78466 0.723984 8.76288 0.615062 8.72057 0.513513C8.67827 0.411964 8.61627 0.319796 8.53817 0.242327Z"
          fill="black"
        />
        <path
          d="M8.53817 8.96275C8.38203 9.11796 8.17082 9.20508 7.95066 9.20508C7.73051 9.20508 7.5193 9.11796 7.36316 8.96275L4.3715 6.01275L1.4215 8.96275C1.26536 9.11796 1.05415 9.20508 0.833999 9.20508C0.613844 9.20508 0.402634 9.11796 0.246499 8.96275C0.168392 8.88528 0.106396 8.79311 0.0640889 8.69157C0.0217817 8.59002 0 8.48109 0 8.37108C0 8.26107 0.0217817 8.15215 0.0640889 8.0506C0.106396 7.94905 0.168392 7.85689 0.246499 7.77942L3.77983 4.24608C3.8573 4.16798 3.94947 4.10598 4.05102 4.06368C4.15257 4.02137 4.26149 3.99959 4.3715 3.99959C4.48151 3.99959 4.59043 4.02137 4.69198 4.06368C4.79353 4.10598 4.8857 4.16798 4.96316 4.24608L8.53817 7.77942C8.61627 7.85689 8.67827 7.94905 8.72057 8.0506C8.76288 8.15215 8.78466 8.26107 8.78466 8.37108C8.78466 8.48109 8.76288 8.59002 8.72057 8.69157C8.67827 8.79311 8.61627 8.88528 8.53817 8.96275Z"
          fill="black"
        />
      </svg>
      Retour
    </button>
    <BookingSelect
      :options="options"
      :model-value="localJourneyType"
      :is-booking-home="isBookingHome"
      :class="{ glass: isBookingHome }"
      :booking-mode-error="bookingModeError"
      @mode-changed="modeChangedHandler"
    />
    <BookingServicePicker
      v-if="localJourneyType === 'service'"
      v-model="localServiceFamily"
      :options="servicesOptions"
      :class="{ glass: isBookingHome }"
      :width="searchBarWidth"
      :booking-service-picker-error="bookingServicePickerError"
      :popover-target="servicePickerPopoverTarget"
      @service-changed="serviceChangedHandler"
    />
    <BookingPlaces
      ref="bookingPlaces"
      :local-service-family="localServiceFamily"
      :local-journey-type="localJourneyType"
      :class="{ glass: isBookingHome }"
      :is-booking-home="isBookingHome"
      :booking-places-error="bookingPlacesError"
      :placeholder-text="placeholderText"
      @input-value="handleInputValue"
      @press-enter="searchHandler"
    />
    <div class="booking-button">
      <AnimatedButton
        :class="{ glass: isBookingHome }"
        :label="searchBarCta"
        :disabled="isDisabledSearchCta"
        @click="searchHandler"
      />
    </div>
  </div>
</template>

<script setup>
import { ref, computed, onMounted, nextTick, watchEffect } from "vue";
import { useQuery } from "villus";
import { useRouter, useRoute } from "vue-router";
import { graphqlSearchbarGetServicesAndSettings } from "../utils/queryGraphql";

// stores
import { useBookingStore } from "../stores/booking";
import { usePlaceStore } from "../stores/place";
import { useTranslationsStore } from "../stores/translations";

// components
import BookingSelect from "./BookingSelect.vue";
import BookingPlaces from "./BookingPlaces.vue";
import BookingServicePicker from "./BookingServicePicker.vue";
import AnimatedButton from "./AnimatedButton.vue";

// composables
import { useViewport } from "../composables/useViewport";

// stores
const translationsStore = useTranslationsStore();
const bookingStore = useBookingStore();
const placeStore = usePlaceStore();

// refs
const bookingSearchBar = ref(null);
const searchBarWidth = ref(0);
const bookingPlaces = ref(null);
const localJourneyType = ref("center");
const localServiceFamily = ref(null);
const bookingPlacesError = ref(false);
const bookingServicePickerError = ref(false);
const bookingModeError = ref(false);
const placeInputValue = ref("");
const placeholderText = ref("");
const searchBarCta = ref("");
const route = useRoute();
const router = useRouter();

defineProps({
  isBookingHome: Boolean,
});

const isDesktop = useViewport().isDesktop;

const servicePickerPopoverTarget = ref("#booking-app");

const emit = defineEmits(["searchSubmit"]);

// journey type selection center/spa
const options = computed(() => [
  {
    type: "group",
    label: translationsStore.t("search_bar_select_group_label"),
    key: translationsStore.t("search_bar_select_group_label"),
    children: [
      {
        label: translationsStore.t("search_bar_select_label_spa"),
        value: "center",
      },
      {
        label: translationsStore.t("search_bar_select_label_treatment"),
        value: "service",
      },
    ],
  },
]);

const modeChangedHandler = (newMode) => {
  console.log("newMode = ", newMode);
  console.log("journeyType in store = ", bookingStore.journeyType);
  localJourneyType.value = newMode;
  bookingPlaces.value.emptyInput();
  placeStore.updateInput("");
  placeStore.updatePlace({});
};

const isValidInput = (input) => {
  // whole text is made of 1 to 4 numbers
  const regexA = /^\d{1,4}$/;
  // whole text is made of 6 numbers or more
  const regexB = /^\d{6,}$/;
  // text contains special characters
  const regexC = /[$&+:;=?@#|<>.^*()%!_]/;
  // text contains same character repeated at least 3 times
  const regexD = /([A-Za-z])\1{2,}/;
  // whole text is only whitespace
  const regexE = /^\s*$/;

  if (
    input.trim() === "" ||
    regexA.test(input.trim()) ||
    regexB.test(input.trim()) ||
    regexC.test(input.trim()) ||
    regexD.test(input.trim()) ||
    regexE.test(input.trim())
  ) {
    return false;
  }

  return true;
};

const searchHandler = async () => {
  console.log("localJourneyType.value", localJourneyType.value);
  console.log("placeInputValue.value.trim()", placeInputValue.value.trim());
  console.log("localServiceFamily.value", localServiceFamily.value);

  // Reset errors
  bookingPlacesError.value = false;
  bookingServicePickerError.value = false;
  bookingModeError.value = false;

  // Reset store
  bookingStore.updateCurrentCenter(null);
  bookingStore.updateCurrentService(null);

  if (localJourneyType.value === "") {
    bookingModeError.value = true;
    return;
  } else if (localJourneyType.value === "service") {
    if (!isValidInput(placeInputValue.value) || !localServiceFamily.value) {
      if (!localServiceFamily.value) bookingServicePickerError.value = true;
      if (!isValidInput(placeInputValue.value)) bookingPlacesError.value = true;
      return;
    }
  } else {
    if (!isValidInput(placeInputValue.value)) {
      bookingPlacesError.value = true;
      return;
    }
  }

  const input = placeInputValue.value.trim();
  const { AutocompleteSuggestion, Place } =
    await window.google.maps.importLibrary("places");

  const countries = JSON.parse(settings.value.countries.value) || [];
  const includedRegionCodes = countries.slice(0, 15);
  const includedPrimaryTypes = [
    "administrative_area_level_1",
    "administrative_area_level_2",
    "administrative_area_level_3",
    "locality",
  ];

  const autocompleteRequest = {
    input,
    includedPrimaryTypes,
    includedRegionCodes,
  };

  try {
    const { suggestions } =
      await AutocompleteSuggestion.fetchAutocompleteSuggestions(
        autocompleteRequest
      );

    if (!suggestions || suggestions.length === 0) {
      console.error("No predictions found");
      bookingPlacesError.value = true;
      emit("searchSubmit", bookingPlacesError.value);
      return;
    }

    const firstSuggestion = suggestions[0];
    const placeId = firstSuggestion.placePrediction.placeId;

    const place = new Place({ id: placeId });

    let result;
    try {
      result = await place.fetchFields({
        fields: ["location", "formattedAddress", "displayName", "id"],
      });
    } catch (err) {
      console.error("Place.fetchFields error", err);
      result = null;
    }

    if (result) {
      placeStore.updatePlace(result.place);
    } else {
      placeStore.updatePlace({ name: input });
    }

    placeStore.updateInput(input);

    const { lat, lng } = placeStore.latLng;
    const storedPlaceId = placeStore.placeId;

    let lang = "";
    if (typeof route !== "undefined") {
      lang = route.params.lang === "en" ? "en" : "";
    }

    bookingStore.resetStore();
    bookingStore.journeyType = localJourneyType.value;

    if (bookingStore.journeyType === "service") {
      bookingStore.updateServiceFamily(localServiceFamily.value);
    }

    const urlPrefix =
      window.location.pathname === "/" ? "" : `${window.location.pathname}`;
    const baseUrl = import.meta.env.VITE_BASE_URL.replace(/\/$/, "");

    window.dataLayer = window.dataLayer || [];
    if (typeof router !== "undefined") {
      window.dataLayer?.push({
        event: "booking-search",
        booking_search_type: localJourneyType.value,
        booking_search_product: localServiceFamily.value || "",
        booking_search_location: placeInputValue.value.trim(),
      });
    }

    if (bookingStore.journeyType === "center") {
      if (typeof router !== "undefined") {
        router.push({
          name: "BookingByCenters",
          params: { lang },
          query: { placeId: storedPlaceId, lat, lng, step: 1 },
        });
      } else {
        window.location.assign(
          `${urlPrefix}${baseUrl}/booking-by-centers?placeId=${storedPlaceId}&lat=${lat}&lng=${lng}&step=1`
        );
      }
    } else if (bookingStore.journeyType === "service") {
      if (typeof router !== "undefined") {
        router.push({
          name: "BookingByServices",
          params: { lang },
          query: {
            placeId: storedPlaceId,
            lat,
            lng,
            step: 1,
            serviceFamily: localServiceFamily.value,
          },
        });
      } else {
        window.location.assign(
          `${urlPrefix}${baseUrl}/booking-by-services?placeId=${storedPlaceId}&lat=${lat}&lng=${lng}&step=1&serviceFamily=${localServiceFamily.value}`
        );
      }
    }
  } catch (err) {
    console.error("AutocompleteSuggestion error", err);
    bookingPlacesError.value = true;
  }

  emit("searchSubmit", bookingPlacesError.value);
};

// center/service search
// const buttonHandler = () => {
//   console.log(bookingStore)
//   console.log()
//   // if (serviceFamily.value) {
//   //   bookingStore.updateServiceFamily(serviceFamily.value);
//   // }
//   // Commit only on submit
//   bookingStore.resetStore()
//   bookingStore.journeyType = localJourneyType.value;
//   bookingStore.serviceFamily = localServiceFamily.value;
//   // if(bookingStore.journeyType == "center") {
//   //   bookingStore.serviceFamily = null;
//   // }
//   // console.log("PLacestore.placeId = ", placeStore.placeId);
//   if (placeStore.placeId) {
//     emit("searchbarButtonPressed");
//   } else {
//     window.alert("veuillez indiquer un lieu");
//   }

// };

const { onData } = useQuery({
  query: graphqlSearchbarGetServicesAndSettings,
});

const servicesOptions = ref({});
const settings = ref({});

onData((data) => {
  // --- Group services by category ---
  const servicesByCategory = Object.groupBy(
    data.otherServices?.nodes,
    ({ category }) => JSON.parse(category.value)[0]
  );

  const categories = Object.values(servicesByCategory);

  // --- Build the base services options (without highlights yet) ---
  const baseOptions = categories.map((category) => {
    return {
      type: "group",
      label: JSON.parse(category[0].category.value)[0],
      key: JSON.parse(category[0].category.value)[0],
      children: category.map((item) => {
        const label = item.fields.find((field) => field.key === "name").value;
        return {
          label,
          value: label,
        };
      }),
    };
  });

  // --- Build highlights group (if exists) ---
  let highlightsGroup = null;
  if (data.highlights?.nodes?.length) {
    const highlightOptions = data.highlights.nodes.map((highlight) => {
      return {
        label: highlight.name.value,
        value: highlight.name.value,
        pictogram: highlight.pictogram?.reference?.image?.url ?? null,
      };
    });

    highlightsGroup = {
      type: "group",
      label: "Highlights",
      key: "highlights",
      children: highlightOptions,
    };
  }

  // --- Put highlights first ---
  servicesOptions.value = highlightsGroup
    ? [highlightsGroup, ...baseOptions]
    : baseOptions;

  console.log("servicesOptions =", JSON.stringify(servicesOptions.value));
  console.log("servicesOptions array length =", servicesOptions.value.length);

  settings.value = data.settings.nodes[0];
});
console.log("servicesOptions = ", servicesOptions.value);
const serviceChangedHandler = (newService) => {
  localServiceFamily.value = newService;
};

onMounted(() => {
  if (typeof route === "undefined" && isDesktop.value) {
    servicePickerPopoverTarget.value = "#booking-searchbar";
  } else if (typeof route === "undefined" && !isDesktop.value) {
    servicePickerPopoverTarget.value = ".template-index";
  } else {
    servicePickerPopoverTarget.value = "#booking-app";
  }
  if (route.query.serviceFamily) {
    bookingStore.updateServiceFamily(route.query.serviceFamily);
  }
  localJourneyType.value =
    bookingStore.journeyType !== "" ? bookingStore.journeyType : "center";
  localServiceFamily.value = bookingStore.serviceFamily;

  if (route?.name === "BookingByCenters") {
    bookingStore.journeyType = "center";
  } else if (route?.name === "BookingByServices") {
    bookingStore.journeyType = "service";
  }

  nextTick(() => {
    if (bookingSearchBar.value) {
      searchBarWidth.value = bookingSearchBar.value.offsetWidth;
    }
  });
});
const togglePlacesPanel = () => {
  if (bookingPlaces.value) {
    bookingPlaces.value.shouldShowDefaultPanel = false; // or toggle if needed
  }
};

const handleInputValue = (value) => {
  placeInputValue.value = value;
};

const isDisabledSearchCta = computed(() => {
  return false;
  /*
  if (localJourneyType.value === "service") {
    return (
      placeInputValue.value.trim() === "" ||
      localServiceFamily.value === "" ||
      localServiceFamily.value === undefined ||
      localServiceFamily.value === null
    );
  } else {
    return placeInputValue.value.trim() === "";
  }
   */
});

watchEffect(() => {
  if (localJourneyType.value !== "") {
    bookingModeError.value = false;
    placeholderText.value =
      localJourneyType.value === "service"
        ? translationsStore.t("search_bar_autocomplete_placeholder_service")
        : translationsStore.t("search_bar_autocomplete_placeholder");
  }
  if (localServiceFamily.value !== "") {
    bookingServicePickerError.value = false;
  }
  if (placeInputValue.value.trim() !== "") {
    bookingPlacesError.value = false;
  }
  console.log("placeStore.input = ", placeStore.input);
  /*
  if (placeStore.input !== "") {
    placeInputValue.value = placeStore.input;
  }
   */
  searchBarCta.value = translationsStore.t("search_bar_cta");
});
</script>

<style scoped>
.search-bar {
  position: relative;
  margin: 0 1.25rem;
  box-sizing: border-box;
  grid-auto-flow: column;
  grid-template-columns: auto 1fr auto;
  grid-gap: 1px;
  background: transparent;
  max-width: 57rem;
  /* border: 1px solid rgba(0 0 0 / 10%); */
}

.search-bar.glass {
  padding-block: 2rem;
  background-color: rgb(var(--brand-white) / 1);
  /* From https://css.glass */
}
.booking-button {
  justify-self: stretch;
  margin: 1rem 0;
}
.back-btn {
  display: flex;
  align-items: center;
  gap: 1rem;
  border: none;
  background: transparent;
  text-transform: uppercase;
  font-size: 0.625rem;
  font-weight: 600;
  margin-bottom: 1.5rem;
  svg {
    width: 0.5625rem;
    height: 0.5625rem;
  }
}
@media screen and (max-width: 999px) {
  .search-bar.glass {
    border: 1px solid rgba(0 0 0 / 10%);
    padding-inline: 1.5rem;
  }
}
@media screen and (min-width: 1000px) {
  .search-bar {
    margin: 0 auto;
    display: flex;
    /*align-items: center;*/
    max-height: calc(3.5rem + 2px);
    border: 1px solid rgba(0 0 0 / 10%);
  }
  .search-bar.glass {
    max-height: 4rem;
    padding-block: 0;
    background: rgba(0 0 0 / 15%);
    box-shadow: 0 4px 30px rgba(0 0 0 / 10%);
    backdrop-filter: blur(12px);
    -webkit-backdrop-filter: blur(12px);
    border: none;
  }
  .booking-button {
    margin: 0.5rem 0.5rem 0.5rem 0;
  }
  .booking-button button {
    padding-block: 0;
    min-height: 2.5rem;
  }
  .booking-button button:focus-visible {
    outline: 1px solid rgb(var(--brand-primary) / 1);
  }
  .glass .booking-button {
    margin: 0.75rem;
  }
}
:deep(.places) {
  flex-grow: 1;
}
</style>
